import ContinueButton from "@components/ContinueButton";
import Error from "@hudson/ui/components/Error";
import Button from "@hudson/ui/components/FormFields/Button";
import Password from "@hudson/ui/components/FormFields/Password";
import Username from "@hudson/ui/components/FormFields/Username";
import ConcentricLoader from "@hudson/ui/components/Loader/ConcentricLoader";
import { FormProvider, useForm } from "@hudson/ui/components/ReactHookForm";
import Success from "@hudson/ui/components/Success";
import { dehtml } from "@hudson/ui/lib/utils";
import useContent from "@lib/use_content";
import loginProfile from "@services/login_profile";
import { useSearchParams } from "next/navigation";
import { useState } from "react";

interface LoginProps {
  defaultEmail?: string;
  onGotoSendResetPage: () => void;
  onSuccessfulLogin: () => void;
}

function Login({ defaultEmail, onGotoSendResetPage, onSuccessfulLogin }: LoginProps) {
  const searchParams = useSearchParams();
  const { data, loading } = useContent([
    "beErrFieldRequired",
    "beForgotPwButton",
    "beLoginTitle",
    "beCreateNewAccountButton",
    "beLoginSubmitButton",
    "bePasswordLabel",
    "beUsernameLabel",
    "beErrUserLogin",
    "bePasswordAriaHide",
    "bePasswordAriaShow",
    "beRequiredPlaceholder",
  ]);
  const formMethods = useForm({ defaultValues: { username: defaultEmail } });
  const [errorMessage, setErrorMessage] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const loginMessage = searchParams?.get("login_message");

  interface LoginData {
    username: string;
    password: string;
  }

  async function submitForm(data: LoginData) {
    setErrorMessage(false);
    setSubmitting(true);

    let result = await loginProfile({ id: data.username, password: data.password, type: "USER" });

    if (!result.success) {
      setErrorMessage(true);
      setSubmitting(false);
    } else {
      setSubmitting(false);
      onSuccessfulLogin();
    }
  }

  return (
    <>
      {loading ? (
        <ConcentricLoader className="max-w-2xl px-4 mx-auto opacity-60 sm:py-10 sm:px-6 lg:px-8" />
      ) : (
        <FormProvider {...formMethods}>
          <form
            className="flex flex-col items-center max-w-xs py-8 mx-auto sm:py-10 gap-y-6 sm:gap-x-4"
            id="login"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              formMethods.handleSubmit(submitForm)();
            }}
            noValidate
          >
            <h2 className="flex flex-wrap items-center mb-3 text-3xl font-extrabold md:text-3xl opacity-70">
              {dehtml(data?.content.beLoginTitle)}
            </h2>

            {loginMessage && <Success message={loginMessage} heavyStyle scrollTo />}

            {errorMessage && (
              <Error className="mt-2" message={data?.content.beErrUserLogin} heavyStyle scrollTo />
            )}

            <Username
              autoFocus={!defaultEmail}
              errorMessage={data?.content.beErrFieldRequired}
              fieldName="username"
              label={data?.content.beUsernameLabel}
              requiredPlaceholder={data?.content.beRequiredPlaceholder}
            />
            <div>
              <Password
                autoFocus={!!defaultEmail}
                errorMessage={data?.content.beErrFieldRequired}
                fieldName="password"
                hideLabel={data?.content.bePasswordAriaHide}
                label={data?.content.bePasswordLabel}
                requiredPlaceholder={data?.content.beRequiredPlaceholder}
                showLabel={data?.content.bePasswordAriaShow}
              />
              <div className="mt-1">
                <Button asLink onClick={() => onGotoSendResetPage()} type="button">
                  {data?.content.beForgotPwButton}
                </Button>
              </div>
            </div>

            <ContinueButton
              alignLeft
              className="mt-2 justify-self-start"
              disabled={submitting}
              label={data.content.beLoginSubmitButton}
              loading={submitting}
            />
          </form>
        </FormProvider>
      )}
    </>
  );
}

export default Login;
